import { HubConnection } from '@microsoft/signalr';
import { useBunkerContext } from 'src/context/BunkerContext';
import { mapBunkeringBM, mapBunkeringStatusBM, mapFuelRobBMs } from 'src/factory/Bunkering/BunkeringFactory';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { useToast } from './useToast';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { getLatestStatus } from 'src/components/bunkering/BunkeringUtils';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';

export const useBunkerUpdateHandler = () => {
    const { toastSuccess } = useToast();
    const { bunkerings, setBunkerings, bunkeringEvents, fuelRobData, setFuelRobData, deletedBunkerings, setDeletedBunkerings } = useBunkerContext();

    const handleUpdatedBunkering = (bm: BunkeringBM) => {
        const _bunkerings = bunkerings ? [...bunkerings] : [];

        // If the bunkering is deleted, remove it from the list of bunkerings related to deleted events
        if (bm.deleted) {
            const _deletedBunkerings = deletedBunkerings.filter((b) => b.bunkeringGuid !== bm.bunkeringGuid);
            setDeletedBunkerings(_deletedBunkerings);
        }

        const bunkeringEvent = bunkeringEvents.find((be) => be.eventID === bm.eventId);
        if (!bunkeringEvent) return;
        const index = _bunkerings.findIndex((b) => b.bunkeringGuid === bm.bunkeringGuid);
        if (index >= 0) {
            _bunkerings[index] = mapBunkeringBM(bm, bunkeringEvent);
        } else {
            _bunkerings.push(mapBunkeringBM(bm, bunkeringEvent));
        }
        setBunkerings(_bunkerings);
    };

    const handleUpdatedBunkeringStatus = (bm: BunkeringStatusBM) => {
        const updatedStatus = mapBunkeringStatusBM(bm);
        const _bunkerings = bunkerings ? [...bunkerings] : [];
        const index = _bunkerings.findIndex((b) => b.bunkeringGuid === bm.bunkeringGuid);
        if (index >= 0) {
            const _bunkering = _bunkerings[index];
            const existingStatus = _bunkering.bunkeringStatuses.find((s) => s.bunkeringStatusId === bm.bunkeringStatusId);
            if (existingStatus) {
                Object.assign(existingStatus, updatedStatus);
            } else {
                _bunkering.bunkeringStatuses.push(updatedStatus);
            }
            const latestStatus = getLatestStatus(_bunkering.bunkeringStatuses);
            _bunkering.currentStatus = latestStatus;
            setBunkerings(_bunkerings);
        }
    };

    const handleDeletedBunkeringStatus = (bunkeringGuid: string) => {
        const _bunkerings = bunkerings ? [...bunkerings] : [];
        const index = _bunkerings.findIndex((b) => b.bunkeringStatuses.some((s) => s.bunkeringStatusGuid === bunkeringGuid));
        if (index >= 0) {
            const _bunkering = _bunkerings[index];
            _bunkering.bunkeringStatuses = _bunkering.bunkeringStatuses.filter((s) => s.bunkeringStatusGuid !== bunkeringGuid);
            const latestStatus = getLatestStatus(_bunkering.bunkeringStatuses);
            _bunkering.currentStatus = latestStatus;
            setBunkerings(_bunkerings);
        }
    };

    const handleUpdatedRobs = (bms: FuelRobBM[], offlineFuelRobs: any[]) => {
        const updatedFuelRobData = mapFuelRobBMs(bms, offlineFuelRobs);
        const _fuelRobData = fuelRobData ? [...fuelRobData] : [];
        updatedFuelRobData.forEach((fuelRob) => {
            const index = _fuelRobData.findIndex(
                (rob) => rob.noonReportId === fuelRob.noonReportId && rob.bunkeringGuid === fuelRob.bunkeringGuid && rob.tankId === fuelRob.tankId
            );
            if (index >= 0) {
                _fuelRobData[index] = fuelRob;
            } else {
                _fuelRobData.push(fuelRob);
            }
        });
        setFuelRobData(_fuelRobData);
    };

    const toggleBunkerToast = (signalRResponse: any, hubConnection: HubConnection, dataType: string) => {
        if (signalRResponse.connectionId !== hubConnection.connectionId) {
            if (signalRResponse.sender) {
                toastSuccess(signalRResponse.sender + ' has updated ' + dataType);
            } else {
                toastSuccess(dataType + ' has been updated');
            }
        }
    };

    return {
        handleUpdatedBunkering,
        handleUpdatedBunkeringStatus,
        handleDeletedBunkeringStatus,
        handleUpdatedRobs,
        toggleBunkerToast,
    };
};
